import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import SettingsSuggestOutlinedIcon from '@mui/icons-material/SettingsSuggestOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

const settingsCards = [
  {
    title: 'Expéditions passées',
    icon: <ScheduleOutlinedIcon color="primary" />,
    status: 'Hardcodé dans le code',
    statusColor: 'warning' as const,
    currentValueLabel: 'Valeur observée actuellement',
    currentValue: 'La home charge actuellement jusqu à 8 expéditions passées antérieures à la date courante.',
    description:
      'L affichage des expéditions passées est actuellement contrôlé dans le code frontend.',
    implementationNote:
      'Le comportement observé repose sur un chargement manuel des archives et sur une limite de 8 résultats dans le code actuel.',
  },
  {
    title: 'Inscription à la newsletter',
    icon: <EmailOutlinedIcon color="primary" />,
    status: 'Activée actuellement',
    statusColor: 'success' as const,
    currentValueLabel: 'Valeur observée actuellement',
    currentValue: 'Bloc newsletter présent sur la page d\'accueil client.',
    description:
      'L affichage de l\'inscription à la newsletter est actuellement contrôlé dans le code.',
    implementationNote:
      'Le bloc inclut un champ email, une case de consentement marketing et un déclenchement d\'enregistrement de consentement depuis la home.',
  },
];

export default function HomeSettingsPage() {
  return (
    <Container maxWidth="lg" sx={{ py: { xs: 14, md: 18 } }}>
      <Stack spacing={4}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
        >
          <Box>
            <Typography
              variant="overline"
              sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: '0.18em' }}
            >
              Administration
            </Typography>
            <Typography variant="h3" component="h1" sx={{ mt: 1, fontStyle: 'italic' }}>
              Paramètres de la home client
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2, maxWidth: 760 }}>
              Cette page documente les paramètres actuellement observés sur la page d'accueil client.
              Ils restent pour l'instant hardcodés dans le frontend. Aucune configuration dynamique
              Firestore ni formulaire d'édition n'ont été ajoutés dans cette version.
            </Typography>
          </Box>

          <Button component={RouterLink} to="/admin" variant="outlined" color="primary">
            Retour au tableau de bord
          </Button>
        </Stack>

        <Alert icon={<InfoOutlinedIcon fontSize="inherit" />} severity="info" variant="outlined">
          Page strictement informative. Aucun champ éditable, aucun bouton d'enregistrement,
          aucune écriture Firestore.
        </Alert>

        <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <SettingsSuggestOutlinedIcon color="primary" />
              <Typography variant="h5">Synthèse</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              L'objectif de cette page est de rendre visibles aux administrateurs certains réglages
              appliqués à la home client, sans introduire encore de couche de configuration dynamique.
            </Typography>
          </Stack>
        </Paper>

        <Stack spacing={3}>
          {settingsCards.map((card) => (
            <Paper
              key={card.title}
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Stack spacing={2.5}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={1.5}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    {card.icon}
                    <Typography variant="h5">{card.title}</Typography>
                  </Stack>
                  <Chip label={card.status} color={card.statusColor} variant="outlined" />
                </Stack>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75 }}>
                    {card.currentValueLabel}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {card.currentValue}
                  </Typography>
                </Box>

                <Alert severity="warning" variant="outlined">
                  {card.description}
                </Alert>

                <Typography variant="body2" color="text.secondary">
                  {card.implementationNote}
                </Typography>
              </Stack>
            </Paper>
          ))}
        </Stack>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 4,
            border: '1px dashed',
            borderColor: 'primary.main',
            bgcolor: 'primary.50',
          }}
        >
          <Stack spacing={1.5}>
            <Typography variant="h6">Évolution prévue</Typography>
            <Typography variant="body2" color="text.secondary">
              Une prochaine version pourra exposer ces paramètres via une configuration admin modifiable,
              mais cette itération se limite volontairement à la documentation de l'état actuel du code.
            </Typography>
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
}